import { Vector2 } from "../../common/vector2.js";
import { Rect } from "../../common/rect.js";
import { Transformable } from "../../transforms/transformable.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Easing } from "../../transforms/transform_base.js";
import { ColorScheme } from "./color_scheme.js";

export type TransformOptions = {
  direction_x?: number;
  direction_y?: number;
  rotation_angle?: number;
  image_opacity?: number;
  darkness_factor?: number;
  pressed_scale?: number;
};

export abstract class UIElement extends Transformable
{
  private static DEFAULT_PRESSED_SCALE: number = 0.95;
  private static PRESS_DURATION: number = 40;
  private static RELEASE_DURATION: number = 40;

  protected ctx: CanvasRenderingContext2D;
  protected rect: Rect;
  private children: UIElement[] = [];
  private onClick?: ( p: Vector2 ) => void;
  private replay_click_on_root = false;
  protected is_pressed: boolean = false;

  private pressed_scale: number;

  private is_part_of_parent_not_a_separate_element: boolean = false;
  private is_button: boolean = false;
  private is_clickable: boolean = true;

  private static onAnyTouchCallback?: ( element: UIElement ) => void;

  protected rotatable: boolean = false;
  protected direction_x: number = 1;
  protected direction_y: number = 1;
  protected rotation_angle: number = 0;
  protected image_opacity: number = 1.0;
  protected darkness_factor: number = 0.0;

  private was_pressed_inside: boolean = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    is_part_of_parent_not_a_separate_element: boolean = false,
    is_scalable_on_click = false,
    options?: TransformOptions
  )
  {
    super( new Vector2( x, y ) );

    this.ctx = ctx;
    this.rect = new Rect( 0, 0, width, height );

    this.is_part_of_parent_not_a_separate_element = is_part_of_parent_not_a_separate_element;
    this.is_button = is_scalable_on_click;
    this.pressed_scale = options?.pressed_scale ?? UIElement.DEFAULT_PRESSED_SCALE;
    this.applyTransformOptions(options);
  }
  protected applyTransformOptions(options?: TransformOptions): void {
    if (options?.direction_x !== undefined && options?.direction_y !== undefined) {
      this.scaleElement(options.direction_x, options.direction_y);
    }

    if (options?.rotation_angle !== undefined) {
      this.setRotationDegrees(options.rotation_angle);
    }

    if (options?.image_opacity !== undefined) {
      this.setImageOpacity(options.image_opacity);
    }

    if (options?.darkness_factor !== undefined) {
      this.setDarknessFactor(options.darkness_factor);
    }
  }

  protected scaleElement(x: number, y: number): void {
    this.direction_x = x;
    this.direction_y = y;
  }

  protected setRotationDegrees(angle_in_degrees: number): void {
    this.rotation_angle = angle_in_degrees * Math.PI / 180;
  }

  public setImageOpacity(opacity: number): void {
    this.image_opacity = opacity;
  }

  protected setDarknessFactor(factor: number): void {
    this.darkness_factor = Math.max( 0, Math.min( 1, factor ) );
  }

  public getDarknessFactor(): number {
    return this.darkness_factor;
  }

  public setClickable( value: boolean ): void
  {
    this.is_clickable = value;
  }

  public getContext(): CanvasRenderingContext2D
  {
    return this.ctx;
  }

  public getAbsolutePosition(): Vector2 {
    let absoluteX = this.pixelPosForRender().x;
    let absoluteY = this.pixelPosForRender().y;

    let current = this.getParentElement();
    while (current) {
      const parentPos = current.pixelPosForRender();
      absoluteX += parentPos.x;
      absoluteY += parentPos.y;
      current = current.getParentElement();
    }

    return new Vector2(absoluteX, absoluteY);
  }

  public getActualOpacity(): number
  {
    let opacity = this.getOpacityForRender();
    let current = this.getParentElement();
    while ( current !== null )
    {
      opacity *= current.getOpacityForRender();
      current = current.getParentElement();
    }
    return opacity;
  }

  public getAbsoluteLayoutForHtml(): { x: number; y: number; scale_x: number; scale_y: number }
  {
    const chain: UIElement[] = [];
    let curr: UIElement | null = this;
    while ( curr !== null )
    {
      chain.unshift( curr );
      curr = curr.getParentElement();
    }

    let canvas_x = 0;
    let canvas_y = 0;
    let cum_sx   = 1;
    let cum_sy   = 1;

    for ( let i = 0; i < chain.length - 1; i++ )
    {
      const elem = chain[ i ];
      const s    = elem.getScaleForRender();
      const px   = elem.pixelPosForRender().x;
      const py   = elem.pixelPosForRender().y;
      const w2   = elem.rect.getW2();
      const h2   = elem.rect.getH2();

      canvas_x += cum_sx * ( px + w2 * ( 1 - s.x ) );
      canvas_y += cum_sy * ( py + h2 * ( 1 - s.y ) );

      cum_sx *= s.x;
      cum_sy *= s.y;
    }

    canvas_x += cum_sx * this.pixelPosForRender().x;
    canvas_y += cum_sy * this.pixelPosForRender().y;

    return { x: canvas_x, y: canvas_y, scale_x: cum_sx, scale_y: cum_sy };
  }

  public getAbsoluteRect(): { x: number, y: number, width: number, height: number } {
    const absPos = this.getAbsolutePosition();
    return {
      x: absPos.x,
      y: absPos.y,
      width: this.rect.getW(),
      height: this.rect.getH()
    };
  }

  private parent_element: UIElement | null = null;

  private getParentElement(): UIElement | null {
    return this.parent_element;
  }

  public setParentElement(parent: UIElement | null): void {
    this.parent_element = parent;
  }

  protected createDarkenedImage( image: ImageBitmap ): HTMLCanvasElement
  {
    const off_canvas = document.createElement( "canvas" );
    off_canvas.width = ( image as any ).width;
    off_canvas.height = ( image as any ).height;
    const off_ctx = off_canvas.getContext( "2d" )!;

    off_ctx.drawImage( image, 0, 0 );

    const image_data = off_ctx.getImageData( 0, 0, off_canvas.width, off_canvas.height );
    const data = image_data.data;

    const darkening_multiplier = 1.0 - this.darkness_factor;

    for ( let i = 0; i < data.length; i += 4 )
    {
      const alpha = data[i + 3];

      if ( alpha > 0 )
      {
        data[i] = Math.floor( data[i] * darkening_multiplier );
        data[i + 1] = Math.floor( data[i + 1] * darkening_multiplier );
        data[i + 2] = Math.floor( data[i + 2] * darkening_multiplier );
      }
    }

    off_ctx.putImageData( image_data, 0, 0 );
    return off_canvas;
  }

  protected applyTransform(): void {
    this.ctx.scale(this.direction_x, this.direction_y);
    this.ctx.rotate(this.rotation_angle);
    this.ctx.globalAlpha *= this.image_opacity;
  }

  public enableRotation()
  {
    this.rotatable = true;
  }

  public removeAllChildren(): void
  {
    this.children = [];
  }

  public hideElement( duration: number = 240, easing: Easing = Easing.EaseOutQuad ): void
  {
    this.fadeOut( duration, undefined, easing );
  }

  public showElement( duration: number = 240, easing: Easing = Easing.EaseInQuad ): void
  {
    this.fadeIn( duration, undefined, easing );
  }

  public forceRender(): void
  {
    this.setShouldBeRendered();
    this.render();
  }

  public forceRedraw(): void
  {
    this.setShouldBeRendered();
  }

  // Alias used by canvas-detached elements (e.g. the leaderboard floating card)
  // that render themselves outside the normal tree walk.
  public requestRender(): void
  {
    this.setShouldBeRendered();
  }

  // Repaints this element and its immediate parent — used when an async asset
  // (e.g. a Facebook avatar photo) arrives after a one-shot render of a scroll
  // row and needs to replace the placeholder in place.
  public forceRenderParent(): void
  {
    this.forceMakeShouldBeRendered();
    if ( this.parent_element )
    {
      this.parent_element.forceMakeShouldBeRendered();
      this.parent_element.render();
    }
    else
    {
      this.render();
    }
  }

  public invalidateSubtree(): void
  {
    this.setShouldBeRendered();

    for ( const child of this.children )
      child.invalidateSubtree();
  }

  public skipRenderSubtree(): void
  {
    this.resetShouldBeRendered();

    for ( const child of this.children )
      child.skipRenderSubtree();
  }

  public hideAndShow( hide_duration: number = 240, show_duration: number = 240, delay: number = 0 ): void
  {
    this.fadeOutAndIn( hide_duration, show_duration, delay );
  }

  public blinkElement( blink_duration: number = 120, blink_count: number = 3 ): void
  {
    const now = performance.now();
    for ( let i = 0; i < blink_count; i++ )
    {
      const fadeOutStart = now + ( i * blink_duration * 2 );
      const fadeInStart = fadeOutStart + blink_duration;

      this.fadeOut( blink_duration, fadeOutStart );
      this.fadeIn( blink_duration, fadeInStart );
    }
  }

  public setOnClick( onClick: ( p: Vector2 ) => void, replay_on_root = false )
  {
    this.onClick = onClick;
    this.replay_click_on_root = replay_on_root;
  }

  public getOnClick(): ( ( p: Vector2 ) => void ) | undefined
  {
    return this.onClick;
  }

  /** Diagnostic snapshot of the hit-test branches at a canvas-local point. */
  public debugTouchHitPath( p: Vector2 ): Record<string, unknown>[]
  {
    const hits: Record<string, unknown>[] = [];
    const visit = ( element: UIElement, parent_point: Vector2, depth: number ): void =>
    {
      const local = element.toLocal( parent_point );
      if ( !element.containsPointInner( local ) )
        return;

      const pos = element.pixelPosForRender();
      hits.push( {
        depth,
        name: element.name(),
        position: { x: pos.x, y: pos.y },
        size: { width: element.rect.getW(), height: element.rect.getH() },
        local_point: { x: local.x, y: local.y },
        clickable: element.is_clickable,
        button: element.is_button,
        has_on_click: element.onClick !== undefined,
        rendered: element.shouldBeRendered(),
      } );

      for ( let i = element.children.length - 1; i >= 0; i-- )
      {
        const child = element.children[i];
        if ( !child.isPartOfParent_notSeparateElement() )
          visit( child, local, depth + 1 );
      }
    };

    visit( this, p, 0 );
    return hits;
  }

  public static setOnAnyTouchCallback( callback: ( element: UIElement ) => void ): void
  {
    UIElement.onAnyTouchCallback = callback;
  }

  public cleanup(): void
  {
    this.children.forEach( child => child.cleanup() );
  }

  protected updateBasic( now: number ): void
  {
    //console.log( `UIElement.updateBasic()   name ${this.name()}   this.shouldBeRendered() ${this.shouldBeRendered()}` );

    this.updateSize( now );

    // Plain loops on purpose: this walks the whole element tree every frame,
    // and a per-call closure here was a steady source of GC pressure.
    for ( let i = 0; i < this.children.length; i++ )
    {
      const child = this.children[i];
      if ( child.isPartOfParent_notSeparateElement() )
        continue;

      child.updateBasic( now );

      if ( child.shouldBeRendered() )
        this.setShouldBeRendered();
    }

    if ( this.shouldBeRendered() )
    {
      for ( let i = 0; i < this.children.length; i++ )
        this.children[i].invalidateSubtree();
    }
  }

  public getRootElement(): UIElement
  {
    let current: UIElement = this;
    while ( current.parent_element )
      current = current.parent_element;
    return current;
  }

  public forceMakeShouldBeRendered()
  {
    this.getRootElement().invalidateSubtree();
  }

  public abstract render(): void;

  public abstract name(): string;

  public renderChildren(): void
  {
    const w2 = this.rect.getW2();
    const h2 = this.rect.getH2();

    this.ctx.translate( -w2, -h2 );

    this.children.forEach( child =>
    {
      child.render();
    } );
  }

  protected renderChildren_forMostTopParent(): void
  {
    this.resetShouldBeRendered();

    const pixel_pos = this.pixelPosForRender();

    this.ctx.save();
    this.ctx.translate( pixel_pos.x + this.rect.centerX(), pixel_pos.y + this.rect.centerY() );

    this.finishRender();
  }

  protected startRender(): void
  {
    this.resetShouldBeRendered();

    const pixel_pos = this.pixelPosForRender();
    const scale     = this.getScaleForRender();
    const opacity   = this.getOpacityForRender();

    this.ctx.save();

    if ( this.rotatable )
    {
      const cx = this.rect.getW() / 2;
      const cy = this.rect.getH() / 2;
      this.ctx.translate( pixel_pos.x + cx, pixel_pos.y + cy );

      const angle = this.getRotationForRender();
      if ( angle !== 0 ) this.ctx.rotate( angle );

      // this.ctx.translate( -cx , -cy );
    }
    else
    {
      this.ctx.translate( pixel_pos.x + this.rect.centerX(), pixel_pos.y + this.rect.centerY() );
    }

    this.ctx.scale( scale.x, scale.y );
    this.ctx.globalAlpha *= opacity;
  }

  // protected startRender(): void
  // {
  //   this.resetShouldBeRendered();

  //   const pixel_pos = this.pixelPosForRender();
  //   const scale = this.getScaleForRender();
  //   const opacity = this.getOpacityForRender();

  //   this.ctx.save();
  //   this.ctx.translate(
  //     pixel_pos.x + this.rect.centerX(),
  //     pixel_pos.y + this.rect.centerY()
  //   );

  //   if ( this.rotatable )
  //   {
  //     const angle = this.getRotationForRender();
  //     if ( angle !== 0 ) this.ctx.rotate( angle );
  //   }

  //   // this.ctx.translate( pixel_pos.x + this.rect.centerX(), pixel_pos.y + this.rect.centerY() );
  //   this.ctx.scale( scale.x, scale.y );
  //   this.ctx.globalAlpha *= opacity;
  // }

  protected finishRender(): void
  {
    this.renderChildren();
    this.ctx.restore();
  }

  public setContextRecursive( ctx: CanvasRenderingContext2D ): void
  {
    this.ctx = ctx;
    this.children.forEach( child => child.setContextRecursive( ctx ) );
  }

  protected isPartOfParent_notSeparateElement() { return this.is_part_of_parent_not_a_separate_element; }

  addChild( elem: UIElement, is_part_of_parent_not_a_separate_element: boolean = false ): void
  {
    if ( is_part_of_parent_not_a_separate_element )
      elem.is_part_of_parent_not_a_separate_element = is_part_of_parent_not_a_separate_element;

    elem.setParentElement( this );
    this.children.push( elem );
  }

  addChildAtIndex( elem: UIElement, index: number, is_part_of_parent_not_a_separate_element: boolean = false ): void
  {
    if ( is_part_of_parent_not_a_separate_element )
      elem.is_part_of_parent_not_a_separate_element = is_part_of_parent_not_a_separate_element;

    elem.setParentElement( this );
    this.children.splice( index, 0, elem );
  }

  childIndexOf( elem: UIElement ): number
  {
    return this.children.indexOf( elem );
  }

  removeChild( elem: UIElement ): void
  {
    const index = this.children.indexOf( elem );
    if ( index > -1 )
    {
      elem.setParentElement( null );
      this.children.splice( index, 1 );
    }
  }

  protected containsPointInner( p: Vector2 ): boolean
  {
    const w = this.rect.getW();
    const h = this.rect.getH();

    //console.log( `containsPointInner( p ${p.x},${p.y} )   w ${w}  h ${h}   name ${this.name()}` );

    return p.x >= 0
      && p.y >= 0
      && p.x <= w
      && p.y <= h;
  }

  protected toLocal( p: Vector2 ): Vector2
  {
    const pixel_pos = this.pixelPosForRender();
    return new Vector2( p.x - pixel_pos.x, p.y - pixel_pos.y );
    //return new Vector2( p.x - this.pixel_pos.x - this.rect.getX(), p.y - this.pixel_pos.y - this.rect.getY() );
  }

  // Рекурсивная обработка клика/тача (событие пробегается по дереву)
  public onTouchStart( p: Vector2, now: number ): boolean
  {
    if ( !this.is_clickable )
      return false;

    /*
        const dbg_text = document.getElementById( 'dbgText' );
        if ( dbg_text )
          dbg_text.innerText = `onTouchStart(${p.toString()})`;
    */
    const local_p: Vector2 = this.toLocal( p );
    //console.log( `UIElement.onTouchStart( p ${p.toString()})   local_p ${local_p.toString()}   rect ${this.rect.toStr()}   name ${this.name()}` );
    if ( !this.containsPointInner( local_p ) )
      return false;

    if ( UIElement.onAnyTouchCallback )
    {
      UIElement.onAnyTouchCallback( this );
    }

    //console.log( `UIElement.onTouchStart()   name ${this.name()}   children.length ${this.children.length}    is_part_of_parent_not_a_separate_element ${this.is_part_of_parent_not_a_separate_element}` );
    // Сначала проверяем дочерние элементы (обратный порядок – верхние слои первыми)
    for ( let i = this.children.length - 1; i >= 0; i-- )
    {
      const child = this.children[i];
      if ( child.isPartOfParent_notSeparateElement() )
        continue;

      if ( child.onTouchStart( local_p, now ) )
        return true;
    }

    // ни один из детей не обработал событие
    //console.log( `this.is_button ${this.is_button}   name ${this.name()}` );
    if ( this.is_button )
    {
      this.pressButton( now );
    }

    this.was_pressed_inside = true;

    return true;
  }

  public getChildren(): readonly UIElement[]
  {
    return this.children;
  }

  public onTouchEnd( p: Vector2, now: number, p_global: Vector2 | null = null ): void
  {
    /*
        const dbg_text = document.getElementById( 'dbgText' );
        if ( dbg_text )
          dbg_text.innerText = `onTouchEnd(${p.toString()})`;
    */
    const local_p: Vector2 = this.toLocal( p );
    //if ( !this.containsPoint( local_p ) )
    {
      if ( this.is_button )
        this.releaseButton( now );
      //return false;
    }

    // Сначала проверяем дочерние элементы (обратный порядок – верхние слои первыми)
    for ( let i = this.children.length - 1; i >= 0; i-- )
    {
      const child = this.children[i];
      if ( !child )
        continue;
      child.onTouchEnd( local_p, now, p_global ? p_global : p );
    }
    //console.log(`p_global ${p_global}   p ${p}   local_p ${local_p}   name ${this.name()}` );

    // ни один из детей не обработал событие.
    //this.releaseButton( now );
    if ( this.was_pressed_inside && this.containsPointInner( local_p ) && this.onClick )
    {
      const click_point = p_global ? p_global : p;
      const replay_root = this.replay_click_on_root ? this.getRootElement() : null;
      this.onClick( click_point );

      // Dismiss layers are removed by onClick. Replay after the current event
      // finishes so the now-exposed element receives a complete down/up tap.
      if ( replay_root && replay_root !== this )
      {
        queueMicrotask( () =>
        {
          const replay_now = performance.now();
          console.log( "[TooltipTapDebug] local dismiss replay", {
            point: { x: click_point.x, y: click_point.y },
            dismiss_element: this.name(),
            root: replay_root.name(),
            hit_path: replay_root.debugTouchHitPath( click_point ),
          } );
          replay_root.onTouchStart( click_point, replay_now );
          replay_root.onTouchEnd( click_point, replay_now );
        } );
      }
    }

    this.was_pressed_inside = false;
  }

  public onTouchMove( p: Vector2, now: number ): void
  {
    const local_p: Vector2 = this.toLocal( p );
    if ( !this.containsPointInner( local_p ) )
    {
      if ( this.is_pressed )
        this.releaseButton( now );
      return;
    }

    for ( let i = this.children.length - 1; i >= 0; i-- )
    {
      const child = this.children[i];
      child.onTouchMove( local_p, now );
    }
  }

  /**
   * Cancels an active press without dispatching onClick. Scroll containers use
   * this when a tap turns into a drag, so nested buttons return from their
   * pressed bump state while the scrolling gesture keeps ownership.
   */
  public cancelTouch( now: number ): void
  {
    if ( this.is_pressed )
      this.releaseButton( now );

    this.was_pressed_inside = false;

    for ( const child of this.children )
      child.cancelTouch( now );
  }

  private pressButton( now: number ): void
  {
    this.is_pressed = true;
    if (this.shouldAnimatePress()) {
      this.animatePress( now );
    }
  }

  protected shouldAnimatePress(): boolean
  {
    return true;
  }
  private releaseButton( now: number ): void
  {
    if ( !this.is_pressed )
      return;

    this.is_pressed = false;
    if (this.shouldAnimatePress()) {
      this.animateRelease( now );
    }
  }

  private updateSize( now: number )
  {
    //if ( !this.isAnimating() )
    //  return;

    //if ( now < this.anim_start_time )
    //  return;

    this.updateAllTransforms( now );
    this.clearFinishedTransforms_andCallOnFinished( now );
  }

  // Простейшая анимация «вдавливания» (уменьшение/увеличение)
  // Press feedback is a two-step snap (down / up), not a tween: every tween
  // frame repaints the whole host canvas, so a single tap used to cost ~15
  // full-canvas rasters — the fps dip felt on every button press.
  private animatePress( now: number ): void
  {
    this.setScaleImmediate( new Vector2( this.pressed_scale, this.pressed_scale ) );
    // Keep the bump, but redraw through the root so scroll-container clipping
    // and transforms are applied. Drawing the immediate parent directly can
    // leak scrolled content into the safe-area strip.
    this.forceMakeShouldBeRendered();
  }
  private animateRelease( now: number ): void
  {
    this.setScaleImmediate( new Vector2( 1, 1 ) );
    this.forceMakeShouldBeRendered();
  }
  public getRect(): Rect
  {
    return this.rect;
  }

  protected drawRectAreaForTest(): void
  {
    this.ctx.beginPath();
    this.ctx.fillStyle = ColorScheme.black_transparent;
    this.ctx.rect( -this.rect.getW2(), -this.rect.getH2(), this.rect.getW(), this.rect.getH() );
    this.ctx.fill();
  }
}
